// FE-SCRM-062 — Manual Form 5500 sponsor link modal.
//
// Surfaces from the <BenefitsIntelligencePanel> empty state on the
// Prospect detail page when the auto-match in DATA-SCRM-017 failed
// (no EIN, ambiguous name+state, or simply nothing in the form5500
// reference data that lines up).
//
// The modal is mostly a typeahead. The host page owns the link
// mutation + query invalidation; the modal calls back via ``onLink``
// and stays open if the link fails so the operator can pick again or
// read the error banner without losing their place.

import { useEffect, useRef, useState } from "react";

import { Modal } from "@/design-system";

import { CrmFormBanner } from "../components";

import type { Form5500SponsorSuggestion } from "./types";


export interface LinkForm5500SponsorModalProps {
  open: boolean;
  /** Used as the modal title hint so the producer knows which
   *  prospect they're linking. */
  prospectName?: string;
  onClose: () => void;
  /** Hits ``GET /api/v1/form5500/sponsors?q=`` (or any equivalent
   *  injected fetcher). Returning ``[]`` is fine. */
  onSearch: (q: string) => Promise<Form5500SponsorSuggestion[]>;
  /** Hits the existing ``/link-form5500`` endpoint via the parent
   *  mutation. Throw to surface the BE's ``detail`` slug — the
   *  modal humanises the known set. */
  onLink: (sponsor: Form5500SponsorSuggestion) => Promise<unknown>;
}


// Match the BE short-circuit at MIN_QUERY_CHARS in
// backend/form5500/views.py.
const MIN_QUERY = 2;
const DEBOUNCE_MS = 200;


// Producer-facing rewrites for known BE error slugs returned by
// /link-form5500. Anything not in this map falls back to the raw
// detail (humanised by replacing underscores with spaces).
const ERROR_HUMANIZER: Record<string, string> = {
  sponsor_not_found:
    "That sponsor record is no longer available — pick another from"
    + " the search results.",
  not_found:
    "This prospect couldn't be loaded. Refresh the page and try"
    + " again.",
  forbidden:
    "You don't have permission to link this prospect.",
  auth_required: "Sign in again to continue.",
};


function humaniseError(detail: string | undefined): string {
  if (!detail) {
    return "Something went wrong. Try again or pick a different sponsor.";
  }
  if (detail in ERROR_HUMANIZER) return ERROR_HUMANIZER[detail];
  return detail.replaceAll("_", " ");
}


function extractDetail(err: unknown): string | undefined {
  if (err && typeof err === "object" && "body" in err) {
    const body = (err as { body?: { detail?: string } }).body;
    if (body && typeof body.detail === "string") return body.detail;
  }
  return undefined;
}


export function LinkForm5500SponsorModal({
  open, prospectName, onClose, onSearch, onLink,
}: LinkForm5500SponsorModalProps): JSX.Element | null {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Form5500SponsorSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | undefined>();

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  // Re-arm the cancellation flag on every (re)mount — without this,
  // React 18 StrictMode's double-invoke leaves cancelled=true.
  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  // Reset on close so a re-open starts fresh.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSearching(false);
      setLinking(null);
      setErrorDetail(undefined);
      if (timer.current) clearTimeout(timer.current);
    }
  }, [open]);

  function handleQueryChange(next: string) {
    setQuery(next);
    setErrorDetail(undefined);
    if (timer.current) clearTimeout(timer.current);
    const trimmed = next.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults([]);
      setSearching(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await onSearch(trimmed);
        if (!cancelled.current) setResults(rows);
      } catch {
        if (!cancelled.current) setResults([]);
      } finally {
        if (!cancelled.current) setSearching(false);
      }
    }, DEBOUNCE_MS);
  }

  async function handleSelect(sponsor: Form5500SponsorSuggestion) {
    setLinking(sponsor.id);
    setErrorDetail(undefined);
    try {
      await onLink(sponsor);
      // Caller is responsible for closing on success — they invalidate
      // queries first, then close so the panel re-fetches before fade.
    } catch (err) {
      setErrorDetail(extractDetail(err) ?? "unknown");
    } finally {
      setLinking(null);
    }
  }

  if (!open) return null;

  const banner = errorDetail
    ? [{ message: humaniseError(errorDetail) }]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        prospectName
          ? `Link ${prospectName} to a Form 5500 sponsor`
          : "Link Form 5500 sponsor"
      }
      size="lg"
    >
      <div
        data-testid="link-f5500-sponsor-modal"
        className="flex flex-col gap-3"
      >
        <p className="text-xs text-text-secondary">
          Search the DOL Form 5500 filings for the right sponsor.
          Linking unlocks fit score, carrier lineup, and change alerts
          for this prospect.
        </p>

        <input
          type="text"
          autoFocus
          data-testid="link-f5500-search-input"
          placeholder="Search by name or EIN…"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          className={[
            "w-full rounded-md border border-border-default bg-raised",
            "px-3 py-2 text-sm text-text-primary",
            "placeholder:text-text-tertiary outline-none",
            "transition-fast ease-out",
            "focus-visible:border-teal focus-visible:shadow-focus",
          ].join(" ")}
        />

        {query.trim().length === 1 && (
          <p
            data-testid="link-f5500-min-hint"
            className="text-xs text-text-tertiary"
          >
            Type at least 2 characters to search.
          </p>
        )}

        {errorDetail && (
          <div data-testid="link-f5500-error-banner">
            <CrmFormBanner errors={banner} />
          </div>
        )}

        {searching && results.length === 0 && (
          <p className="text-xs text-text-tertiary">Searching…</p>
        )}

        {!searching && query.trim().length >= MIN_QUERY
          && results.length === 0 && !errorDetail && (
          <p
            data-testid="link-f5500-empty"
            className="text-xs text-text-tertiary"
          >
            No matching sponsors. Try a shorter or different name, or
            search by EIN.
          </p>
        )}

        {results.length > 0 && (
          <ul
            data-testid="link-f5500-suggestions"
            role="listbox"
            className={[
              "max-h-72 overflow-auto rounded-md border",
              "border-border-subtle bg-raised divide-y divide-border-subtle",
            ].join(" ")}
          >
            {results.map((s) => (
              <li
                key={s.id}
                role="option"
                aria-selected={false}
                data-testid={`link-f5500-suggestion-${s.id}`}
                onClick={() => {
                  if (linking === null) handleSelect(s);
                }}
                className={[
                  "flex cursor-pointer flex-col gap-0.5 px-3 py-2",
                  "text-sm hover:bg-recessed",
                  linking === s.id ? "opacity-60" : "",
                ].join(" ")}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-text-primary">
                    {s.name}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    EIN {s.ein}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2 text-xs text-text-secondary">
                  <span>
                    {s.state ?? "—"}
                    {s.latest_filing_year !== null
                      ? ` · Last filed ${s.latest_filing_year}`
                      : ""}
                  </span>
                  <span>
                    {s.participant_count !== null
                      ? `${s.participant_count.toLocaleString()} participants`
                      : "Participants unknown"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
